/**
 * scaleBarcodeParser.js
 * Utilitário centralizado e modular para decodificação de códigos de barras EAN-13
 * emitidos por balanças comerciais (Toledo Prix, Filizola, Urano, Balmak, etc.).
 *
 * Suporte a múltiplos módulos de balança:
 * 1. 'toledo': Toledo Prix / Padrão MGV (Preço Total Embutido: 2 CCCCC VVVVVV D)
 * 2. 'legacy_weight': Balanças por Peso Líquido (Legado / Filizola / Urano / Toledo Peso: 2 CCCCC PPPPP D)
 */

export const SCALE_MODULES = [
  {
    id: 'toledo',
    name: 'Toledo Prix (Preço Total - Padrão MGV)',
    shortName: 'Toledo (Preço)',
    description: 'Etiquetas EAN-13 com código do item (5 dígitos) e valor total em centavos (6 dígitos)',
    mode: 'price',
  },
  {
    id: 'legacy_weight',
    name: 'Balança por Peso Líquido (Legado / Filizola / Urano)',
    shortName: 'Peso Líquido',
    description: 'Etiquetas EAN-13 com código do item e peso em gramas embutido (4, 5 ou 6 dígitos de PLU)',
    mode: 'weight',
  },
];

const PREFERENCE_KEY = 'duzz_scale_module';

/**
 * Retorna o módulo de balança configurado pelo usuário (padrão: 'toledo')
 */
export function getScaleModulePreference() {
  try {
    return localStorage.getItem(PREFERENCE_KEY) || 'toledo';
  } catch {
    return 'toledo';
  }
}

/**
 * Salva a preferência do módulo de balança do usuário
 */
export function setScaleModulePreference(moduleId) {
  try {
    localStorage.setItem(PREFERENCE_KEY, moduleId || 'toledo');
  } catch (err) {
    console.warn('Erro ao salvar preferência de módulo de balança:', err);
  }
}

/**
 * MÓDULO 1: Toledo Prix - Preço Total (Padrão MGV)
 * Layout: 2 CCCCC VVVVVV D
 * - 2: Prefixo indicador de produto pesável/interno (1 dígito)
 * - CCCCC: Código do produto / PLU (5 dígitos correspondentes ao ID do produto)
 * - VVVVVV: Valor total da etiqueta em centavos (6 dígitos, ex: 001599 = R$ 15,99)
 * - D: Dígito verificador EAN-13 (1 dígito)
 */
export function parseToledoPriceBarcode(rawCode) {
  if (!rawCode) return null;
  const code = String(rawCode).trim();

  if (code.length === 13 && code.startsWith('2') && /^\d{13}$/.test(code)) {
    const pluStr = code.substring(1, 6);
    const priceStr = code.substring(6, 12);
    const plu = parseInt(pluStr, 10);
    const totalPrice = parseInt(priceStr, 10) / 100;

    if (plu > 0 && totalPrice > 0) {
      return {
        module: 'toledo',
        mode: 'price',
        rawCode: code,
        plu,
        pluStr,
        totalPrice,
        checkDigit: code[12],
      };
    }
  }

  return null;
}

/**
 * MÓDULO 2: Balança por Peso Líquido (Legado / Filizola / Urano / Toledo por Peso)
 * Suporta os múltiplos formatos posicionais clássicos:
 * - PLU 5 dígitos + Peso 5 dígitos (pos 1-6 / pos 6-11)
 * - PLU 5 dígitos + Peso 6 dígitos (pos 1-6 / pos 6-12)
 * - PLU 4 dígitos + Peso 6 dígitos (pos 1-5 / pos 5-11)
 * - PLU 6 dígitos + Peso 5 dígitos (pos 1-7 / pos 7-12)
 */
export function parseLegacyWeightBarcode(rawCode) {
  if (!rawCode) return null;
  const code = String(rawCode).trim();

  if (code.length === 13 && code.startsWith('2') && /^\d{13}$/.test(code)) {
    const candidates = [
      { plu: code.substring(1, 6), weight: parseInt(code.substring(6, 11), 10) / 1000 },
      { plu: code.substring(1, 6), weight: parseInt(code.substring(6, 12), 10) / 1000 },
      { plu: code.substring(1, 5), weight: parseInt(code.substring(5, 11), 10) / 1000 },
      { plu: code.substring(1, 7), weight: parseInt(code.substring(7, 12), 10) / 1000 },
    ].filter((c) => c.weight > 0 && parseInt(c.plu, 10) > 0);

    if (candidates.length > 0) {
      return {
        module: 'legacy_weight',
        mode: 'weight',
        rawCode: code,
        candidates: candidates.map((c) => ({
          plu: parseInt(c.plu, 10),
          pluStr: c.plu,
          weight: Math.round(c.weight * 10000) / 10000,
        })),
        checkDigit: code[12],
      };
    }
  }

  return null;
}

/**
 * Parser de balança padrão retrocompatível.
 * Por padrão analisa via Toledo Prix (Preço Total), ou via módulo especificado.
 */
export function parseScaleBarcode(rawCode, options = {}) {
  const preferredModule = options.module || getScaleModulePreference();

  if (preferredModule === 'legacy_weight') {
    return parseLegacyWeightBarcode(rawCode) || parseToledoPriceBarcode(rawCode);
  }

  // Padrão: Toledo primeiro, com fallback para peso legado
  return parseToledoPriceBarcode(rawCode) || parseLegacyWeightBarcode(rawCode);
}

/**
 * Calcula a quantidade proporcional (peso em kg ou unidades) a partir do valor total
 * da etiqueta de balança e do preço unitário cadastrado no produto.
 */
export function calculateScaleItemQuantity(totalPrice, unitPrice) {
  const price = parseFloat(unitPrice);
  const total = parseFloat(totalPrice);

  if (isNaN(price) || price <= 0 || isNaN(total) || total <= 0) {
    return 1;
  }

  // Tratamento contra imprecisão binária de ponto flutuante (IEEE 754)
  const rawRatio = (total / price) * 10000;
  const roundedQty = Math.round(rawRatio + Number.EPSILON) / 10000;
  return Math.max(0.001, roundedQty);
}

/**
 * Resolvedor completo de balança que busca no catálogo utilizando módulos
 * com suporte a fallback inteligente.
 *
 * @param {string} rawCode Código de barras bruto escaneado
 * @param {Function} findProductFn Função (plu: number, pluStr: string) => product | null
 * @param {Object} options { preferredModule: 'toledo' | 'legacy_weight' }
 * @returns {Object|null} { matched: true, module, mode, product, quantity, totalPrice, rawCode }
 */
export function resolveScaleBarcodeMatch(rawCode, findProductFn, options = {}) {
  if (!rawCode || typeof findProductFn !== 'function') return null;

  const preferredModule = options.preferredModule || getScaleModulePreference();

  // Define a ordem dos módulos a serem avaliados (prioridade para o preferido, com fallback para o outro)
  const modulesToTry =
    preferredModule === 'legacy_weight'
      ? ['legacy_weight', 'toledo']
      : ['toledo', 'legacy_weight'];

  for (const mod of modulesToTry) {
    if (mod === 'toledo') {
      const toledoData = parseToledoPriceBarcode(rawCode);
      if (toledoData) {
        const product = findProductFn(toledoData.plu, toledoData.pluStr);
        if (product) {
          const unitPrice = parseFloat(product.valueUn || product.value || 0);
          const quantity = calculateScaleItemQuantity(toledoData.totalPrice, unitPrice);
          return {
            matched: true,
            module: 'toledo',
            mode: 'price',
            product,
            quantity,
            totalPrice: toledoData.totalPrice,
            rawCode: toledoData.rawCode,
          };
        }
      }
    } else if (mod === 'legacy_weight') {
      const weightData = parseLegacyWeightBarcode(rawCode);
      if (weightData) {
        for (const candidate of weightData.candidates) {
          const product = findProductFn(candidate.plu, candidate.pluStr);
          if (product) {
            const unitPrice = parseFloat(product.valueUn || product.value || 0);
            return {
              matched: true,
              module: 'legacy_weight',
              mode: 'weight',
              product,
              quantity: candidate.weight,
              totalPrice: Math.round(candidate.weight * unitPrice * 100) / 100,
              rawCode: weightData.rawCode,
            };
          }
        }
      }
    }
  }

  return null;
}
